import {
  cn,
  Image,
  KdsDatePicker,
  KdsFocusableElement,
  KdsText,
  KdsTooltip,
  toDateValue,
} from "@kazaar-platform/design-system";
import { RiArticleLine, RiCalendarScheduleLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { Controller, useFormContext } from "react-hook-form";

import { getHighlitedDates } from "@/helpers/dates/getHighlitedDates";
import { getDatePickerErrorMessage } from "@/helpers/forms";
import { getTomorrow } from "@/helpers/getTomorrow";
import { getUserLocale } from "@/helpers/lang";
import { userAtom } from "@/store";

import { type OrderItemConfigurationSchema } from "../OrderItemConfigForm.schema";
import { type ProductSectionProps } from "./ProductSection.types";

export const ProductSection = ({
  sandboxItem,
  isCatalogMediumDeleted,
  onScrollToBrief,
}: ProductSectionProps) => {
  // Hooks
  const { t } = useTranslate();
  const userAtomValue = useAtomValue(userAtom);

  // Form context
  const methods = useFormContext<OrderItemConfigurationSchema>();

  // Product Name
  const productName = methods.getValues("name");

  // Handle Scroll to brief
  const handleScrollToBrief = () => {
    onScrollToBrief();
    setTimeout(
      () =>
        document
          .getElementById("brief")
          ?.scrollIntoView({ behavior: "smooth" }),
      50
    );
  };

  return (
    <div
      className={cn(
        "mb-sp-xs flex items-start gap-sp-xl",
        isCatalogMediumDeleted && "pb-4"
      )}
    >
      <div className="object-cover object-center">
        <Image
          alt={productName}
          src={sandboxItem.imageUrl}
          className="max-h-44 min-h-44 min-w-44 max-w-44 rounded-lg"
        />
      </div>

      <div className="flex flex-col items-start justify-center gap-sp-md self-stretch">
        {/* Product info */}
        <div className="flex flex-col gap-sp-xs2">
          <div className="flex items-center gap-sp-xs">
            <KdsText size="title-md" dataCy="productName">
              {productName}
            </KdsText>

            {!isCatalogMediumDeleted && (
              <KdsTooltip alt={t("orderItemConfig.seeBrief")} placement="top">
                <KdsFocusableElement>
                  <RiArticleLine onClick={handleScrollToBrief} />
                </KdsFocusableElement>
              </KdsTooltip>
            )}
          </div>

          {!!sandboxItem.productionTime && !isCatalogMediumDeleted && (
            <div className="flex items-center gap-sp-xs">
              <RiCalendarScheduleLine className="size-sp-md text-warning-text" />
              <KdsText color="warning-text" size="caption-sm">
                {t("orderItemConfig.productionTime", {
                  count: sandboxItem.productionTime,
                })}
              </KdsText>
            </div>
          )}
        </div>

        {/* Delivery date input */}
        {!isCatalogMediumDeleted && (
          <Controller
            name="deliveryDate"
            control={methods.control}
            render={({ field, fieldState }) => (
              <KdsDatePicker
                calendarProps={{
                  firstAvailableDate: getTomorrow(),
                  highlightedDates: getHighlitedDates(
                    sandboxItem.productionTime
                  ),
                  locale: getUserLocale({ locale: userAtomValue?.lang }),
                  tooltipMessage: t(
                    "order.catalogMediumConfiguration.stepDelivery.deliveryBlock.tooltipLabel"
                  ),
                }}
                dataCy="deliveryDatePicker"
                defaultValue={
                  field.value
                    ? toDateValue(field.value) || undefined
                    : undefined
                }
                errorMessage={getDatePickerErrorMessage(
                  fieldState.error?.message
                )}
                hasError={!!fieldState.error}
                label={t("orderItemConfig.deliveryDate")}
                onChange={(value) => {
                  const date = value?.toDate("utc");
                  field.onChange(date);
                }}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};
